import { useState } from "react";
import { Send } from "lucide-react";
import { BroadcastDialog } from "@/components/broadcasts/BroadcastDialog";
import {
  useBroadcastFilters,
  useBroadcastPreview,
  useBroadcastSettings,
} from "@/hooks/useBroadcasts";

/**
 * "Enviar mensaje", after the last filter in the Pipeline.
 *
 * Renders nothing at all unless the tenant is authorized — plan, the school's
 * own switch, and the WhatsApp transport, all answered server-side by
 * `/broadcasts/settings` because the CRM cannot learn the transport on its own.
 *
 * The recipient count rides in the label so nobody has to open the dialog just
 * to find out, and the over-cap explanation is an inline line rather than a
 * tooltip: Radix tooltips do not fire on a disabled trigger and never fire on
 * touch, so a disabled button with an invisible reason is a bug report.
 */
export function BroadcastButton(): React.JSX.Element | null {
  const [open, setOpen] = useState(false);
  const { data: settings } = useBroadcastSettings();
  const filters = useBroadcastFilters();
  const { data: preview, isPending } = useBroadcastPreview({
    filters,
    enabled: settings?.authorized === true,
  });

  if (!settings?.authorized) return null;

  const total = preview?.total ?? 0;
  const overCap = preview?.overCap ?? false;
  const cap = settings.maxRecipients;
  const empty = !isPending && total === 0;
  const disabled = isPending || empty || overCap;

  return (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          setOpen(true);
        }}
        className="inline-flex h-[38px] items-center gap-2 rounded-md border border-ink-100 px-4 text-sm font-semibold text-ink-700 hover:bg-ink-050 disabled:cursor-not-allowed disabled:opacity-40"
      >
        <Send size={15} />
        {isPending ? "Enviar mensaje" : `Enviar mensaje (${String(total)})`}
      </button>

      {overCap ? (
        <p className="text-xs text-amber-700">
          Los filtros seleccionan {total} prospectos. El máximo por envío es {cap} —
          acota los filtros para continuar.
        </p>
      ) : empty ? (
        <p className="text-xs text-ink-500">
          No hay prospectos que coincidan con los filtros.
        </p>
      ) : null}

      <BroadcastDialog
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        filters={filters}
      />
    </div>
  );
}
